import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import * as serviceWorker from './serviceWorker';
import { fetchLatestAppAssetSignature, getCurrentAppAssetSignature } from './utils/appVersion';
import './styles/tailwind.css';

const Root: React.FC = () => {
  const [waitingRegistration, setWaitingRegistration] = React.useState<ServiceWorkerRegistration | null>(null);
  const [isApplyingUpdate, setIsApplyingUpdate] = React.useState(false);
  const [hasVersionUpdate, setHasVersionUpdate] = React.useState(false);
  const currentAssetSignatureRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    serviceWorker.register({
      onUpdate: (registration) => {
        setWaitingRegistration(registration);
        setIsApplyingUpdate(false);
      },
    });
  }, []);

  React.useEffect(() => {
    if (!waitingRegistration) return undefined;

    const handleControllerChange = () => {
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [waitingRegistration]);

  const forceReloadToLatestVersion = React.useCallback(async () => {
    if ('caches' in window) {
      try {
        const cacheKeys = await window.caches.keys();
        await Promise.all(cacheKeys.map((cacheKey) => window.caches.delete(cacheKey)));
      } catch (error) {
        console.error('Failed to clear caches before reload:', error);
      }
    }

    window.location.reload();
  }, []);

  const applyUpdate = React.useCallback(() => {
    setIsApplyingUpdate(true);

    const waitingWorker = waitingRegistration?.waiting;
    if (!waitingWorker) {
      void forceReloadToLatestVersion();
      return undefined;
    }

    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    return undefined;
  }, [forceReloadToLatestVersion, waitingRegistration]);

  const checkForDeployedUpdate = React.useCallback(async () => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    const currentSignature = currentAssetSignatureRef.current || getCurrentAppAssetSignature();
    if (!currentAssetSignatureRef.current) {
      currentAssetSignatureRef.current = currentSignature;
    }

    try {
      const latestSignature = await fetchLatestAppAssetSignature();
      if (!latestSignature) {
        return;
      }

      if (!currentAssetSignatureRef.current) {
        currentAssetSignatureRef.current = latestSignature;
        return;
      }

      if (latestSignature === currentAssetSignatureRef.current) {
        return;
      }

      setHasVersionUpdate(true);
      navigator.serviceWorker.getRegistration().then((registration) => {
        registration?.update().catch((error) => {
          console.error('Error while refreshing service worker registration:', error);
        });
      });
    } catch (error) {
      console.error('Error while checking the deployed app version:', error);
    }
  }, []);

  React.useEffect(() => {
    currentAssetSignatureRef.current = getCurrentAppAssetSignature();

    if (process.env.NODE_ENV !== 'production') {
      return undefined;
    }

    void checkForDeployedUpdate();

    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        void checkForDeployedUpdate();
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void checkForDeployedUpdate();
      }
    }, 60_000);

    window.addEventListener('focus', handleVisible);
    document.addEventListener('visibilitychange', handleVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleVisible);
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, [checkForDeployedUpdate]);

  React.useEffect(() => {
    if ((!waitingRegistration && !hasVersionUpdate) || isApplyingUpdate) return undefined;

    const timer = window.setTimeout(() => {
      void applyUpdate();
    }, 1200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [applyUpdate, hasVersionUpdate, isApplyingUpdate, waitingRegistration]);

  return (
    <App
      hasUpdate={Boolean(waitingRegistration) || hasVersionUpdate}
      isApplyingUpdate={isApplyingUpdate}
    />
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(<Root />);
